import { ExceptionDimensions, ResponseCode, getObjectKeys, logAnalyticsEvent } from '@vestico/utils';

import { RecordInteractionRequest, UserLikePostStatusRequest, UserLikePostStatusResponse } from '@vestico/model';

import { AuthorizedRequestInfo, InteractionsApi } from '../model/api';
import { AbstractApiClient } from './apiClient';
import { createAPIRequestURL, fetchRequest } from './helpers';

export class InteractionsApiClient extends AbstractApiClient implements InteractionsApi {
  public async recordInteraction(request: RecordInteractionRequest, authRequestInfo: AuthorizedRequestInfo): Promise<boolean> {
    const apiEndpoint = '/track/interaction';
    const success = await this.sendRequest<boolean>(apiEndpoint, 'POST', request, authRequestInfo);
    return !!success;
  }

  public async getUserLikedPost(request: UserLikePostStatusRequest, authRequestInfo: AuthorizedRequestInfo): Promise<boolean> {
    const apiEndpoint = '/user-like-post';
    const status = await this.sendRequest<UserLikePostStatusResponse>(apiEndpoint, 'GET', request, authRequestInfo);
    return !!status && status.liked;
  }

  async sendRequest<T>(
    apiEndpoint: string,
    method: 'POST' | 'GET',
    body: UserLikePostStatusRequest | RecordInteractionRequest,
    authRequestInfo: AuthorizedRequestInfo
  ): Promise<T | undefined> {
    if (!authRequestInfo) {
      console.error(`${apiEndpoint} missing required entries`);
      logAnalyticsEvent({
        category: 'exception',
        event: `interaction-precondition`,
        name: `apiEndpoint missing required entries`,
      });
      return undefined;
    }

    const fetchURL = await createAPIRequestURL(authRequestInfo, apiEndpoint);

    if (!fetchURL) {
      console.error(`cannot create record event ${apiEndpoint} URL`);

      logAnalyticsEvent({
        category: 'exception',
        event: `interaction-precondition`,
        name: `Cannot create URL ${apiEndpoint}`,
      });
      return undefined;
    }

    try {
      if (method === 'GET') {
        for (const key of getObjectKeys(body)) {
          const value = body[key];
          if (value) {
            fetchURL.searchParams.append(key, value);
          }
        }
      }

      const result = await fetchRequest({
        url: fetchURL.toString(),
        method,
        body: method !== 'GET' ? body : undefined,
        iOSBundleIdentifier: this.iOSBundleIdentifier,
        authRequestInfo,
        keepalive: true,
      });

      if (result.status !== ResponseCode.success) {
        console.error(`Interaction ${apiEndpoint} returned error ${result.status}`);
        logAnalyticsEvent({
          category: 'exception',
          event: `interaction-event-${apiEndpoint}`,
          name: `${result.status}`,
          dimensions: { description: await result.text() } as ExceptionDimensions,
        });
        return undefined;
      }

      const response = (await result.json()) as T;
      return response;
    } catch (error) {
      console.error(error);
      logAnalyticsEvent({
        category: 'exception',
        event: `interaction req`,
        name: error.message,
      });
      return undefined;
    }
  }
}
